// frontend/src/pages/DataWorkspacePage/DataWorkspacePage.jsx
import React, { useState, useCallback } from 'react';
import ConnectionsSidebar from './ConnectionsSidebar';
import GridEditor from './GridEditor';
import VisualQueryBuilder from './VisualQueryBuilder';
import AutomationsTab from './AutomationsTab.jsx';
import DataStudio from './DataStudio.jsx';
import { apiFetch } from '../../api/index.js';
import { useNotification } from '../../context/NotificationContext.jsx';
import { DynamicIcon } from '../../components/icons.jsx';

/**
 * Unified Data Workspace — main layout page.
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────────┐
 *   │  Header (breadcrumb + toolbar)                       │
 *   ├─────────────┬────────────────────────────────────────┤
 *   │  Sidebar    │  Main viewport (Grid / SQL editor)     │
 *   │  (20%)      │  (80%)                                 │
 *   └─────────────┴────────────────────────────────────────┘
 */

const DataWorkspacePage = ({ token, currentUser }) => {
    const { addNotification } = useNotification();

    // Active table / connection selection from sidebar
    const [activeSelection, setActiveSelection] = useState(null);
    // { connectionId, tableName, schemaName }

    // Data returned by proxy
    const [gridData, setGridData] = useState(null);
    // { columns: [], rows: [], total: 0, page: 1, page_size: 200 }

    const [isLoading, setIsLoading] = useState(false);
    
    // View Mode: 'explorer' | 'builder' | 'studio' | 'automations'
    const [viewMode, setViewMode] = useState('explorer');

    // Schema cache lifted from sidebar — fed to DataStudio for join resolution
    const [schemaCache, setSchemaCache] = useState({}); // { [connId]: { tables: [] } }

    // -----------------------------------------------------------------------
    // Fetch table data (called by sidebar click or GridEditor pagination)
    // -----------------------------------------------------------------------
    const fetchTableData = useCallback(async (selection, page = 1, pageSize = 200, sortCol = null, sortDir = 'asc', filters = []) => {
        if (!selection?.connectionId || !selection?.tableName) return;
        setIsLoading(true);
        try {
            const result = await apiFetch(
                `/data-workspace/connections/${selection.connectionId}/query/table`,
                token,
                {
                    method: 'POST',
                    body: JSON.stringify({
                        table_name:  selection.tableName,
                        schema_name: selection.schemaName || null,
                        page,
                        page_size:   pageSize,
                        sort_col:    sortCol,
                        sort_dir:    sortDir,
                        filters,
                    }),
                }
            );
            setGridData(result);
        } catch (err) {
            addNotification(`Failed to load table: ${err.message}`, 'error');
        } finally {
            setIsLoading(false);
        }
    }, [token, addNotification]);

    const handleSelectTable = useCallback((selection) => {
        setActiveSelection(selection);
        setGridData(null);
        fetchTableData(selection);
    }, [fetchTableData]);

    const handlePageChange = useCallback((page, pageSize) => {
        fetchTableData(activeSelection, page, pageSize);
    }, [activeSelection, fetchTableData]);

    return (
        <div className="flex h-full overflow-hidden bg-white">
            {/* ── Sidebar ─────────────────────────────────────────────────── */}
            <ConnectionsSidebar
                token={token}
                onSelectTable={handleSelectTable}
                activeSelection={activeSelection}
                onSchemaLoaded={(connId, data) => setSchemaCache(prev => ({ ...prev, [connId]: data }))}
            />

            {/* ── Main Viewport ────────────────────────────────────────────── */}
            <div className="flex flex-col flex-1 overflow-hidden">
                {/* Header bar */}
                <div className="flex items-center justify-between px-6 py-3 border-b border-slate-200 bg-white shrink-0">
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                        <DynamicIcon name="Database" className="w-4 h-4 text-blue-500" />
                        {activeSelection ? (
                            <>
                                <span className="text-slate-500">{activeSelection.connectionName}</span>
                                <span className="text-slate-300">/</span>
                                <span className="text-slate-800 font-semibold">{activeSelection.tableName}</span>
                                {gridData && (
                                    <span className="ml-3 text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                                        {gridData.total.toLocaleString()} rows
                                    </span>
                                )}
                            </>
                        ) : (
                            <span className="text-slate-400 italic">Select a table from the sidebar to begin</span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Tabs */}
                        <div className="flex items-center bg-slate-100/80 p-1 rounded-lg border border-slate-200 mr-4">
                            <button
                                onClick={() => setViewMode('explorer')}
                                className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all ${
                                    viewMode === 'explorer' ? 'bg-white text-blue-600 shadow-sm border border-slate-200/60' : 'text-slate-500 hover:text-slate-700'
                                }`}
                            >
                                Explorer
                            </button>
                            <button
                                onClick={() => setViewMode('studio')}
                                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-semibold transition-all ${
                                    viewMode === 'studio' ? 'bg-white text-violet-600 shadow-sm border border-slate-200/60' : 'text-slate-500 hover:text-slate-700'
                                }`}
                            >
                                <DynamicIcon name="Layers" className="w-3.5 h-3.5" />
                                Data Studio
                            </button>
                            <button
                                onClick={() => setViewMode('builder')}
                                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-semibold transition-all ${
                                    viewMode === 'builder' ? 'bg-white text-blue-600 shadow-sm border border-slate-200/60' : 'text-slate-500 hover:text-slate-700'
                                }`}
                            >
                                <DynamicIcon name="BoxSelect" className="w-3.5 h-3.5" />
                                Query Builder
                            </button>
                            <button
                                onClick={() => setViewMode('automations')}
                                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-semibold transition-all ${
                                    viewMode === 'automations' ? 'bg-white text-blue-600 shadow-sm border border-slate-200/60' : 'text-slate-500 hover:text-slate-700'
                                }`}
                            >
                                <DynamicIcon name="Zap" className="w-3.5 h-3.5" />
                                Automations
                            </button>
                        </div>

                        {activeSelection && viewMode === 'explorer' && (
                            <button
                                onClick={() => fetchTableData(activeSelection)}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 transition-colors"
                            >
                                <DynamicIcon name="RefreshCw" className="w-3 h-3" />
                                Refresh
                            </button>
                        )}
                    </div>
                </div>

                {/* Main Area */}
                <div className="flex-1 overflow-hidden relative">
                    {viewMode === 'studio' ? (
                        <DataStudio
                            token={token}
                            schemaData={Object.values(schemaCache).flatMap(d => d?.tables || [])}
                        />
                    ) : !activeSelection ? (
                        <EmptyState onStudio={() => setViewMode('studio')} />
                    ) : viewMode === 'builder' ? (
                        <VisualQueryBuilder 
                            token={token}
                            connectionId={activeSelection.connectionId}
                            tableName={activeSelection.tableName}
                            schemaName={activeSelection.schemaName}
                        />
                    ) : viewMode === 'automations' ? (
                        <AutomationsTab
                            token={token}
                            connectionId={activeSelection.connectionId}
                            tableName={activeSelection.tableName}
                        />
                    ) : (
                        <GridEditor
                            token={token}
                            connectionId={activeSelection.connectionId}
                            tableName={activeSelection.tableName}
                            schemaName={activeSelection.schemaName}
                            gridData={gridData}
                            isLoading={isLoading}
                            onPageChange={handlePageChange}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

const EmptyState = ({ onStudio }) => (
    <div className="flex flex-col items-center justify-center h-full text-center p-12 bg-slate-50">
        <div className="relative mb-6">
            <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 flex items-center justify-center shadow-sm">
                <DynamicIcon name="DatabaseZap" className="w-12 h-12 text-blue-500/60" />
            </div>
            <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-100 to-purple-100 border border-indigo-200 flex items-center justify-center shadow-sm">
                <DynamicIcon name="Table" className="w-4 h-4 text-indigo-600" />
            </div>
        </div>
        <h3 className="text-xl font-semibold text-slate-800 mb-2">Unified Data Workspace</h3>
        <p className="text-slate-500 max-w-sm text-sm leading-relaxed">
            Browse tables in Explorer, or build cross-table datasets visually in Data Studio.
        </p>
        <div className="mt-6 flex gap-3">
            <div className="text-xs text-slate-400 font-medium bg-white px-3 py-1.5 rounded-full border border-slate-200 shadow-sm flex items-center gap-1">
                <DynamicIcon name="ArrowLeft" className="w-3 h-3" /> Select a table from the left sidebar
            </div>
            <button onClick={onStudio} className="text-xs text-violet-600 font-semibold bg-violet-50 hover:bg-violet-100 px-3 py-1.5 rounded-full border border-violet-200 shadow-sm flex items-center gap-1 transition-colors">
                <DynamicIcon name="Layers" className="w-3 h-3" /> Open Data Studio
            </button>
        </div>
    </div>
);

export default DataWorkspacePage;
